import { CARD_STATE } from './listingCardState';

const PREFIX = 'SubscriptionDetailsModal';

/** Tone of a banner or a fact value. Mirrors ROW_TONE on the card. */
export const SUB_TONE = {
  POSITIVE: 'positive',
  WARNING: 'warning',
  NEGATIVE: 'negative',
  NEUTRAL: 'neutral',
};

/**
 * What a seller can do to a subscription.
 *
 * PLAN, PAYMENT_METHOD and INVOICES go to Stripe's billing portal, which is the right
 * place for them — we are not reimplementing card management. CANCEL and RESUME are
 * the ones that change whether the advertisement stays online, so CANCEL now happens
 * in-app rather than bouncing the seller out to confirm a second time.
 *
 * There is no RENEW: an expired subscription cannot be resumed, so that state shows
 * the plan selector and buys a new one.
 */
export const SUB_ACTION = {
  PLAN: 'plan',
  PAYMENT_METHOD: 'paymentMethod',
  INVOICES: 'invoices',
  CANCEL: 'cancel',
  RESUME: 'resume',
};

const DESTRUCTIVE = [SUB_ACTION.CANCEL];

/**
 * Which fact the fourth cell shows, and what to call it.
 *
 * `expiresAt` is the end of the paid period, so it answers a different question in
 * each state: the next charge while the subscription renews, the day the
 * advertisement comes down once cancelled, and the day it went down once expired.
 * Labelling it "Volgende afschrijving" in all three would be actively misleading.
 */
const DATE_FACT = {
  [CARD_STATE.DRAFT_PAID]: { labelId: 'fact.nextCharge', tone: SUB_TONE.NEUTRAL },
  [CARD_STATE.ONLINE]: { labelId: 'fact.nextCharge', tone: SUB_TONE.NEUTRAL },
  [CARD_STATE.NEGOTIATION]: { labelId: 'fact.nextCharge', tone: SUB_TONE.NEUTRAL },
  [CARD_STATE.CANCEL_SCHEDULED]: { labelId: 'fact.offlineOn', tone: SUB_TONE.WARNING },
  // Grey card, but money still moves — the amber is the point.
  [CARD_STATE.OFFLINE]: { labelId: 'fact.nextCharge', tone: SUB_TONE.WARNING },
  [CARD_STATE.EXPIRED]: { labelId: 'fact.offlineSince', tone: SUB_TONE.NEGATIVE },
  [CARD_STATE.SOLD]: { labelId: 'fact.cancelledOn', tone: SUB_TONE.NEUTRAL },
};

const STATUS_FACT = {
  [CARD_STATE.DRAFT_PAID]: { valueId: 'status.active', tone: SUB_TONE.POSITIVE },
  [CARD_STATE.ONLINE]: { valueId: 'status.active', tone: SUB_TONE.POSITIVE },
  [CARD_STATE.NEGOTIATION]: { valueId: 'status.active', tone: SUB_TONE.POSITIVE },
  [CARD_STATE.CANCEL_SCHEDULED]: { valueId: 'status.cancelScheduled', tone: SUB_TONE.WARNING },
  [CARD_STATE.OFFLINE]: { valueId: 'status.active', tone: SUB_TONE.POSITIVE },
  [CARD_STATE.EXPIRED]: { valueId: 'status.expired', tone: SUB_TONE.NEGATIVE },
  [CARD_STATE.SOLD]: { valueId: 'status.cancelled', tone: SUB_TONE.NEUTRAL },
};

/** A sentence at the top, where the facts alone would leave the seller guessing. */
const BANNER = {
  // Billing started at payment, not at publication. Nudge them to use what they are
  // already paying for.
  [CARD_STATE.DRAFT_PAID]: { id: 'banner.draftPaid', tone: SUB_TONE.WARNING },
  [CARD_STATE.CANCEL_SCHEDULED]: { id: 'banner.cancelScheduled', tone: SUB_TONE.WARNING },
  [CARD_STATE.OFFLINE]: { id: 'banner.offline', tone: SUB_TONE.WARNING },
  [CARD_STATE.EXPIRED]: { id: 'banner.expired', tone: SUB_TONE.NEGATIVE },
};

const ACTIONS_BY_STATE = {
  [CARD_STATE.DRAFT_PAID]: [
    SUB_ACTION.PLAN,
    SUB_ACTION.PAYMENT_METHOD,
    SUB_ACTION.INVOICES,
    SUB_ACTION.CANCEL,
  ],
  [CARD_STATE.ONLINE]: [
    SUB_ACTION.PLAN,
    SUB_ACTION.PAYMENT_METHOD,
    SUB_ACTION.INVOICES,
    SUB_ACTION.CANCEL,
  ],
  [CARD_STATE.NEGOTIATION]: [
    SUB_ACTION.PLAN,
    SUB_ACTION.PAYMENT_METHOD,
    SUB_ACTION.INVOICES,
    SUB_ACTION.CANCEL,
  ],
  // Undoing leads; changing the plan is hidden because a cancellation is already
  // pending and stacking a plan change on top of it is a state nobody can reason about.
  [CARD_STATE.CANCEL_SCHEDULED]: [
    SUB_ACTION.RESUME,
    SUB_ACTION.PAYMENT_METHOD,
    SUB_ACTION.INVOICES,
  ],
  [CARD_STATE.OFFLINE]: [
    SUB_ACTION.PLAN,
    SUB_ACTION.PAYMENT_METHOD,
    SUB_ACTION.INVOICES,
    SUB_ACTION.CANCEL,
  ],
  // Nothing to manage: an expired subscription is over, and the dialog shows the plan
  // selector instead of this list — see needsNewSubscription below.
  [CARD_STATE.EXPIRED]: [],
  // Archived: nothing left to manage, but the paperwork stays reachable.
  [CARD_STATE.SOLD]: [SUB_ACTION.INVOICES],
};

/**
 * Everything the "Abonnement beheren" dialog shows, for one advertisement.
 *
 * Derived from the same CARD_STATE the card itself uses, so the dialog can never
 * disagree with the row the seller just read to get here.
 *
 * @param {Object} args
 * @param {string} args.state a CARD_STATE
 * @param {boolean} [args.hasCancelFailed] a cancellation that did not go through
 * @returns {{ banner, facts, actions, showFailure }}
 */
export const deriveSubscriptionDialog = ({
  state,
  hasCancelFailed = false,
  hasCancelScheduled = false,
  hasLiveBilling = false,
}) => {
  // Before payment there is no subscription to manage, and after archiving there is
  // nothing to change — the entry point should not exist, but be safe.
  const isManageable = !!STATUS_FACT[state];

  if (!isManageable) {
    return {
      banner: null,
      facts: [],
      actions: [],
      showFailure: false,
      isManageable: false,
      needsNewSubscription: false,
    };
  }

  /**
   * An archived advertisement whose subscription never actually stopped.
   *
   * Sold does not imply cancelled — the cancellation is a separate call and it can
   * fail. This dialog described every archived advertisement as "Opgezegd" regardless,
   * so a seller who arrived here from the red "cancelling did not work" strip was told
   * by the dialog that it had worked after all, and offered nothing but invoices.
   * The strip's "Probeer opnieuw" opens this dialog, so that was a dead end.
   */
  const showsUnstoppedBilling = state === CARD_STATE.SOLD && hasLiveBilling;

  // Not past tense while the money is still moving: "Laatste plan" for a subscription
  // that is about to charge again reads as though it were over.
  const usesPastTense =
    state === CARD_STATE.EXPIRED || (state === CARD_STATE.SOLD && !hasLiveBilling);

  /**
   * A cancellation pending in a state that is not CANCEL_SCHEDULED.
   *
   * That state is only reachable for a *published* advertisement, so a paid draft
   * whose subscription had been cancelled was still described as a healthy running
   * subscription: status "Actief" in green, a next-charge date that would never be
   * charged, a banner promising that same charge, and an offer to cancel something
   * already cancelled. Every one of those four told the seller the opposite of what
   * had just happened.
   *
   * Already-ended states are excluded: there is nothing pending about them.
   */
  const showsPendingCancellation =
    hasCancelScheduled && !usesPastTense && state !== CARD_STATE.CANCEL_SCHEDULED;

  const dateFact = showsUnstoppedBilling
    ? { labelId: 'fact.nextCharge', tone: SUB_TONE.NEGATIVE }
    : showsPendingCancellation
    ? { labelId: 'fact.runsUntil', tone: SUB_TONE.WARNING }
    : DATE_FACT[state];
  const statusFact = showsUnstoppedBilling
    ? { valueId: 'status.active', tone: SUB_TONE.NEGATIVE }
    : showsPendingCancellation
    ? { valueId: 'status.cancelScheduled', tone: SUB_TONE.WARNING }
    : STATUS_FACT[state];
  const banner = showsUnstoppedBilling
    ? { id: 'banner.soldStillCharged', tone: SUB_TONE.NEGATIVE }
    : showsPendingCancellation
    ? { id: 'banner.cancelledRunsUntil', tone: SUB_TONE.WARNING }
    : BANNER[state] || null;

  return {
    isManageable: true,
    /**
     * There is nothing left to manage — the only way back online is buying a new
     * subscription — so the dialog shows the plan selector rather than facts.
     *
     * An expired subscription is over in Stripe's terms, and Stripe cannot revive one:
     * `/reactivate` only lifts a *pending* cancellation. Offering "Abonnement
     * verlengen" here sent the seller to a billing-portal link that could not resolve,
     * and once the API started answering honestly it became a button whose only
     * possible reply was "this subscription has ended".
     */
    needsNewSubscription: state === CARD_STATE.EXPIRED,
    showFailure: hasCancelFailed,
    banner: banner
      ? { id: `${PREFIX}.${banner.id}`, tone: banner.tone }
      : null,
    facts: [
      {
        key: 'plan',
        labelId: `${PREFIX}.${usesPastTense ? 'fact.lastPlan' : 'fact.plan'}`,
        kind: 'plan',
        tone: SUB_TONE.NEUTRAL,
      },
      { key: 'price', labelId: `${PREFIX}.fact.price`, kind: 'price', tone: SUB_TONE.NEUTRAL },
      {
        key: 'status',
        labelId: `${PREFIX}.fact.status`,
        kind: 'text',
        valueId: `${PREFIX}.${statusFact.valueId}`,
        tone: statusFact.tone,
      },
      {
        key: 'date',
        labelId: `${PREFIX}.${dateFact.labelId}`,
        kind: 'date',
        tone: dateFact.tone,
      },
    ],
    // Undoing leads, and cancelling is gone — you cannot cancel what is already
    // cancelled. Changing the plan goes too: scheduling a plan change on top of a
    // pending cancellation is a state nobody can reason about, which is why
    // CANCEL_SCHEDULED already omits it.
    // Cancelling leads when an archived advertisement is still being charged for:
    // it is the only thing the seller came here to do, and without it the strip's
    // "Probeer opnieuw" opened a dialog offering nothing but invoices.
    actions: (showsUnstoppedBilling
      ? [SUB_ACTION.CANCEL, SUB_ACTION.INVOICES]
      : showsPendingCancellation
      ? [SUB_ACTION.RESUME, SUB_ACTION.PAYMENT_METHOD, SUB_ACTION.INVOICES]
      : ACTIONS_BY_STATE[state] || []
    ).map(key => ({
      key,
      labelId: `${PREFIX}.action.${key}.label`,
      subtitleId: `${PREFIX}.action.${key}.subtitle`,
      isDestructive: DESTRUCTIVE.includes(key),
    })),
  };
};
